// src/components/Header/NavMobile.tsx
import React, { useState } from "react";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import Logo from "shared/Logo/Logo";
import { Disclosure } from "@headlessui/react";
import { NavLink, useNavigate } from "react-router-dom";
import { NavItemType } from "./NavigationItem";
import { NAVIGATION_DEMO_2 } from "data/navigation";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import SocialsList from "shared/SocialsList/SocialsList";
import { ChevronDownIcon, Search } from "lucide-react";
import SwitchDarkMode from "shared/SwitchDarkMode/SwitchDarkMode";

export interface NavMobileProps {
  data?: NavItemType[];
  onClickClose?: () => void;
}

const NavMobile: React.FC<NavMobileProps> = ({
  data = NAVIGATION_DEMO_2,
  onClickClose,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const _renderMenuChild = (
    item: NavItemType,
    itemClass = "pl-3 text-neutral-900 dark:text-neutral-200 font-medium "
  ) => {
    return (
      <ul className="nav-mobile-sub-menu space-y-1 text-sm bg-neutral-50 dark:bg-neutral-800/50 py-2">
        {item.children?.map((i, index) => (
          <Disclosure key={i.href + index} as="li" className="group">
            <NavLink
              to={i.href}
              className={({ isActive }) => {
                const isViewAll = i.name.toLowerCase().includes("view all");
                return `flex w-full cursor-pointer items-center justify-between transition-all duration-200 py-2.5 px-6 ${
                  isActive 
                    ? "text-[#ff6600] font-semibold" 
                    : isViewAll 
                      ? "text-neutral-900 dark:text-neutral-100 font-bold"
                      : "text-neutral-600 dark:text-neutral-400 font-normal"
                }`;
              }}
            >
              <span onClick={onClickClose}>
                {i.name}
              </span>
              {i.children && (
                <span className="flex items-center flex-grow justify-end">
                  <Disclosure.Button
                    as="span"
                    className="flex justify-end flex-grow"
                  >
                    <ChevronDownIcon
                      className="ml-2 h-4 w-4 text-neutral-500 group-hover:rotate-180 transition-transform duration-300"
                      aria-hidden="true"
                    />
                  </Disclosure.Button>
                </span>
              )}
            </NavLink>
            {i.children && (
              <Disclosure.Panel>
                {_renderMenuChild(
                  i,
                  "pl-3 text-slate-600 dark:text-slate-400 "
                )}
              </Disclosure.Panel>
            )}
          </Disclosure>
        ))}
      </ul>
    );
  };

  const _renderItem = (item: NavItemType, index: number) => {
    return (
      <Disclosure
        key={index}
        as="li"
        className="text-slate-900 dark:text-white border-b border-neutral-100 dark:border-neutral-800 last:border-0"
      >
        {item.children ? (
          <Disclosure.Button
            as="div"
            className="flex w-full items-center py-4 px-5 font-medium uppercase tracking-wider text-xs hover:bg-slate-50 dark:hover:bg-slate-800 transition-all duration-200 cursor-pointer"
          >
            <span className="flex-grow">{item.name}</span>
            <ChevronDownIcon
              className="ml-2 h-4 w-4 text-neutral-400 transition-transform duration-300 group-open:rotate-180"
              aria-hidden="true"
            />
          </Disclosure.Button>
        ) : (
          <NavLink
            className={({ isActive }) =>
              `flex w-full cursor-pointer items-center py-4 px-5 font-bold uppercase tracking-wider text-xs hover:bg-slate-50 dark:hover:bg-slate-800 transition-all duration-200 ${
                isActive ? "text-[#ff6600]" : ""
              }`
            }
            to={item.href}
            onClick={onClickClose}
          >
            {item.name}
          </NavLink>
        )}
        {item.children && (
          <Disclosure.Panel>{_renderMenuChild(item)}</Disclosure.Panel>
        )}
      </Disclosure>
    );
  };

  const renderSearchForm = () => {
    const handleSearchSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (searchQuery.trim()) {
        if (onClickClose) onClickClose();
        navigate(`/page-search?q=${encodeURIComponent(searchQuery.trim())}`);
      }
    };

    return (
      <form
        onSubmit={handleSearchSubmit}
        className="flex-1 text-slate-900 dark:text-slate-200"
      >
        <div className="bg-neutral-100 dark:bg-neutral-800 flex items-center space-x-3 py-3 px-4 rounded-2xl border-2 border-transparent focus-within:border-[#ff6600]/20 transition-all duration-200">
          <Search className="w-5 h-5 text-neutral-400" />
          <input
            type="search"
            placeholder="Search products..."
            className="border-none bg-transparent focus:outline-none focus:ring-0 w-full text-sm font-medium placeholder-neutral-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <input type="submit" hidden value="" />
      </form>
    );
  };

  const handleExploreClick = () => {
    if (onClickClose) onClickClose();
    navigate("/page-search");
  };

  return (
    <div className="overflow-y-auto w-full h-screen py-2 transition transform shadow-2xl ring-1 ring-black/5 dark:ring-white/10 bg-white dark:bg-neutral-900">
      <div className="py-8 px-6 relative">
        <Logo />
        <p className="mt-5 text-neutral-500 dark:text-neutral-400 text-[13px] leading-relaxed">
          Premium 3D printing and robotics ecosystem. Empowering innovators through cutting-edge technology.
        </p>

        <div className="flex justify-between items-center mt-8">
          <SocialsList itemClass="w-9 h-9 text-neutral-500 dark:text-neutral-400" />
          <SwitchDarkMode className="!w-10 !h-10 !rounded-xl" />
        </div>
        
        <span className="absolute right-4 top-4">
          <ButtonClose onClick={onClickClose} />
        </span>
      </div>

      <div className="px-6 pb-2">{renderSearchForm()}</div>

      <ul className="flex flex-col py-4 px-0">
        {data.map(_renderItem)}
      </ul>

      <div className="py-8 px-6">
        <ButtonPrimary 
          className="w-full !rounded-2xl py-4 shadow-xl shadow-orange-500/10 hover:shadow-orange-500/20 transform hover:scale-[1.02] transition-all" 
          onClick={handleExploreClick}
        >
          Explore All Products
        </ButtonPrimary>
      </div>
    </div>
  );
};

export default NavMobile;
